/**
 * S11's log terminal — a live tail of what the services are doing.
 *
 * `UI-Design-Spec.md#s11` specifies it: *"full-bleed, dense, monospace, `--bg-inset`, with level
 * as a 3px left rule, filter chips above, and follow-tail on by default with an unmissable
 * paused indicator when scrolled up."* The last clause is the one with a reason attached —
 * *"scrolling up in a live log and not realising it stopped following is a classic
 * incident-response mistake"* — and it is the only thing on this screen that is allowed to
 * shout.
 *
 * ## It is a tail, not an archive, and it says so
 *
 * Nothing is stored. The transport is Redis pub/sub (`jmt_logging.publish`), which is
 * fire-and-forget by design, so this shows what arrived while the screen was open and nothing
 * from before. That is stated on the screen rather than left to be discovered, because a viewer
 * that looks like a log archive and holds ninety seconds is the exact shape this codebase keeps
 * being bitten by: the healthy case and the broken case rendering identically. The durable
 * record is still stdout and the Docker json-file driver.
 *
 * ## Three things that would make it unusable at the real frame rate
 *
 * **One re-render per line.** The engine emits 168 `evaluated` lines at a bar close, each
 * arriving as its own WebSocket message and therefore its own task — React's auto-batching does
 * not span those, so a `setState` per line is 168 renders of a 1,500-row list. Lines land in a
 * ref and a timer promotes them to state at `FLUSH_MS`, which turns the burst into four renders.
 *
 * **Unbounded growth.** A ring of `MAX_LINES`, oldest dropped. Rows are kept to a couple of DOM
 * nodes each so the whole ring can be rendered without virtualising; if that stops being true
 * the fix is windowing, not a smaller ring, because scrollback is the point.
 *
 * **Motion.** New rows do not animate in. The design register's rule is that chrome moves and
 * measurements do not, and a log line is a measurement — but the practical argument is
 * stronger: a fade-in per row at 60 rows/second is a paint storm, and a stream that slides is
 * unreadable while it slides.
 *
 * ## Colour
 *
 * Level is carried by a 3px left rule **and** the level word, never by colour alone. The rule's
 * hues follow `StatusMatrixCell` on this same screen — `--attn` and `--loss` mixed into the
 * inset ground — so the terminal and the data-health matrix directly above it read as one
 * system. That is a deliberate reading of the three colour laws: they reserve amber for money
 * at risk and red for P&L *in the trader-facing surfaces*, and S11 already spends both on
 * operational severity. Flagged for the operator rather than assumed settled.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLiveConnection, useLiveTopic, useLogsDropped } from '../live/LiveProvider';
import { preciseClockOf, useTimestampMode } from '../ui/timestamps';
import {
  appendRows,
  boundaryIndices,
  filterRows,
  formatFields,
  isAtBottom,
  topicFor,
  type LogLine,
  type NewRow,
  type Row,
} from './logStream';

/**
 * How often buffered lines are promoted to state. Below the eye's threshold for "live" and far
 * above the arrival rate, so a burst costs a handful of renders instead of one per line.
 */
const FLUSH_MS = 250;

/** Levels the server will accept as a `logs:` topic argument. Mirrors `topics.LOG_LEVELS`. */
const LEVELS = ['debug', 'info', 'warning', 'error'] as const;
type Level = (typeof LEVELS)[number];

export function LogTerminal(): React.JSX.Element {
  // Once for the whole list, not once per row. At `MAX_ROWS` this is the difference between one
  // subscription and fifteen hundred.
  const timestampMode = useTimestampMode();
  const [level, setLevel] = useState<Level>('info');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [following, setFollowing] = useState(true);
  const { live } = useLiveConnection();

  // Lines arrive faster than React should render. They land here and the timer below promotes
  // them; `nextId` is a plain counter because two lines can share a timestamp to the millisecond
  // and a key collision in a list this long is a rendering bug that looks like a data bug.
  const buffer = useRef<Row[]>([]);
  const nextId = useRef(0);
  // When this terminal opened. Anything stamped earlier than this was handed over on subscribe
  // rather than watched arriving — see `jmt_logging.publish.BACKFILL_SIZE`. A ref, not state:
  // it is read inside `push` and must never cause a render.
  const openedAt = useRef(new Date().toISOString());
  const scroller = useRef<HTMLDivElement | null>(null);

  const push = useCallback((row: NewRow) => {
    // ⚠ **Compared as ISO strings, deliberately.** Both are UTC with millisecond precision and a
    // `Z` suffix, so lexicographic order *is* chronological order — and it stays correct with no
    // `Date` parsing in the hot path, which runs 168 times a bar close.
    const replay = row.kind === 'line' && row.line.ts < openedAt.current;
    buffer.current.push({ ...row, id: nextId.current++, replay });
  }, []);

  useLiveTopic(topicFor(level), (data) => push({ kind: 'line', line: data as unknown as LogLine }));
  useLogsDropped((count) => push({ kind: 'gap', count }));

  useEffect(() => {
    const timer = setInterval(() => {
      if (buffer.current.length === 0) return;
      const incoming = buffer.current;
      buffer.current = [];
      setRows((current) => appendRows(current, incoming));
    }, FLUSH_MS);
    return () => clearInterval(timer);
  }, []);

  // Changing the level re-subscribes, and what is already on screen was selected under the old
  // threshold. Kept rather than cleared: those lines are true, they were observed, and throwing
  // away the history an operator is mid-investigation on to satisfy a filter is the wrong trade.
  // The chip says what is arriving *now*, which is what a live filter can honestly claim.

  const visible = useMemo(() => filterRows(rows, query), [rows, query]);
  // A set, because the render checks membership once per row. Computed from the filtered list so a
  // divider does not appear between two rows the query has just separated.
  const boundaries = useMemo(() => new Set(boundaryIndices(visible)), [visible]);

  // Scroll to the bottom after the rows that caused it have been painted. `useEffect` rather
  // than doing it in the flush: the node has to exist at its new height first.
  useEffect(() => {
    if (!following) return;
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [visible, following]);

  const onScroll = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    // Following resumes on its own when the operator scrolls back down. A tail you have to
    // re-arm with a button is a tail that stays off.
    setFollowing(isAtBottom(node));
  }, []);

  const resume = useCallback(() => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
    setFollowing(true);
  }, []);

  return (
    <section className="jmt-panel jmt-panel--flush jmt-logs">
      <header className="jmt-logs__bar">
        <h2 className="jmt-section-heading jmt-logs__heading">Live log</h2>

        {/* `.jmt-filter`, the chip this app already has, rather than a terminal-specific one.
            The level chips are a filter in exactly the sense that block was written for. */}
        <div className="jmt-filters jmt-logs__chips" role="group" aria-label="Minimum level">
          {LEVELS.map((candidate) => (
            <button
              key={candidate}
              type="button"
              className="jmt-filter"
              data-on={candidate === level ? '' : undefined}
              aria-pressed={candidate === level}
              onClick={() => setLevel(candidate)}
            >
              {candidate}
            </button>
          ))}
        </div>

        <input
          type="search"
          className="jmt-logs__filter"
          placeholder="filter…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Filter lines"
        />

        <span className="jmt-logs__count">
          {visible.length === rows.length
            ? `${rows.length} lines`
            : `${visible.length} of ${rows.length}`}
        </span>
      </header>

      <div
        className="jmt-logs__stream"
        ref={scroller}
        onScroll={onScroll}
        role="log"
        aria-live="off"
        tabIndex={0}
      >
        {rows.length === 0 ? (
          <p className="jmt-logs__empty">
            {live
              ? 'Waiting for the first line. The last few minutes are replayed on connect, then this follows the tail.'
              : 'Not connected. Nothing is arriving.'}
          </p>
        ) : (
          visible.map((row, index) => (
            <Fragment key={row.id}>
              {/* 🔴 The one place the operator is told which lines they were *given* rather than
                  watched arrive. Before F-D82-3 there were none to tell apart, because the
                  terminal was tail-only and opening it mid-hour showed nothing at all. */}
              {boundaries.has(index) ? (
                <p className="jmt-logs__edge">
                  <span>live from here</span>
                </p>
              ) : null}
              {row.kind === 'gap' ? (
                <p className="jmt-logs__gap">
                  — {row.count} {row.count === 1 ? 'line' : 'lines'} dropped: the server could not
                  keep up —
                </p>
              ) : (
                <p className={`jmt-logs__line jmt-logs__line--${row.line.level}`}>
                <span className="jmt-logs__ts">{preciseClockOf(row.line.ts, timestampMode)}</span>
                {/* Spelled out, always. Colour never means anything alone, and the left rule
                    is colour. */}
                <span className="jmt-logs__level">{row.line.level}</span>
                <span className="jmt-logs__service">{row.line.service}</span>
                <span className="jmt-logs__event">{row.line.event}</span>
                <span className="jmt-logs__fields">{formatFields(row.line.fields)}</span>
                  {row.line.truncated ? (
                    <span className="jmt-logs__truncated">truncated</span>
                  ) : null}
                </p>
              )}
            </Fragment>
          ))
        )}
      </div>

      {/* Unmissable, per the spec, and the only thing on S11 permitted to be. It is a button
          because the thing an operator wants when they see it is to get back to the tail. */}
      {!following && rows.length > 0 ? (
        <button type="button" className="jmt-logs__paused" onClick={resume}>
          Paused — not following new lines. Jump to latest
        </button>
      ) : null}

      <p className="jmt-note jmt-logs__note">
        {/* ⚠ **Reworded for F-D82-3, because the previous sentence became false.** It read
            "nothing before this screen opened can be shown", which was true while the terminal was
            tail-only and is now the opposite of what happens: the last few hundred lines are handed
            over on connect. Still not an archive — a ring answers "the last N lines" and the panel
            `UI-Design-Spec.md#s11` describes wants a time filter over a store that does not
            exist. */}
        A tail with a short memory, not an archive. The last few hundred lines are replayed when this
        screen connects; nothing older is kept and the durable log is on the box.{' '}
        {live ? null : 'The socket is closed.'}
      </p>
    </section>
  );
}
